import { useEffect, useRef, useState } from "react";
import { PushPin, CaretDown, Truck, Wrench, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const isoOffset = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
export const PIN_DATES = () => [{ key: isoOffset(0), label: "Today" }, { key: isoOffset(1), label: "Tomorrow" }];
// Formats an ISO date as "Fri Sep 26" so the button can show what the
// admin picked without needing a full calendar re-open.
const shortDateLabel = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
};
export const pinDateLabel = (date) => PIN_DATES().find(d => d.key === date)?.label?.toLowerCase() || shortDateLabel(date);

export default function PinToSweeperButton({ jobIds, workers, disabled, onPinned }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState([]);
  const [date, setDate] = useState(isoOffset(0));
  const [nearest, setNearest] = useState(null);       // { available, truck_key, truck_label, miles, reason }
  const [nearestBusy, setNearestBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/sub-contractors").then(r => setSubs((r.data || []).filter(s => s.sweeper_specialty || s.trade))).catch(() => {});
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Fetch nearest-truck suggestion when the dropdown opens for a SINGLE
  // WO. Bulk pins skip the suggestion because there's no one target to
  // measure from. Re-runs whenever the date toggle changes.
  useEffect(() => {
    if (!open || !Array.isArray(jobIds) || jobIds.length !== 1) { setNearest(null); return; }
    let alive = true;
    setNearestBusy(true);
    api.get(`/jobs/${jobIds[0]}/nearest-truck`, { params: { date } })
      .then(r => { if (alive) setNearest(r.data); })
      .catch(() => { if (alive) setNearest(null); })
      .finally(() => { if (alive) setNearestBusy(false); });
    return () => { alive = false; };
  }, [open, date, jobIds]);

  const pin = async (truck_key, truck_label) => {
    setBusy(true);
    try {
      const r = await api.post("/jobs/sweeper-pin", { job_ids: jobIds, truck_key, truck_label, date });
      if (r.data.pinned === 0) {
        toast.error(r.data.reason || "Nothing was pinned — refresh and try again");
      } else {
        toast.success(`📌 Pinned ${r.data.pinned} WO${r.data.pinned === 1 ? "" : "s"} to ${truck_label} · ${pinDateLabel(date)}`);
        onPinned?.();
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not pin"));
    } finally { setBusy(false); }
  };

  const techs = (workers || []).filter(w => w.role === "worker");
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} disabled={disabled || busy}
        data-testid="kanban-bulk-pin-btn"
        className="h-9 px-3 bg-amber-400 text-slate-900 border-2 border-slate-900 font-black uppercase text-xs tracking-wider inline-flex items-center gap-1 hover:bg-amber-300 disabled:opacity-60">
        <PushPin weight="fill" size={14}/> Pin to Sweeper <CaretDown weight="bold" size={10}/>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-40 w-64 bg-white border-2 border-slate-900 shadow-[6px_6px_0_#0f172a] p-2" data-testid="pin-sweeper-menu">
          <div className="flex gap-1 mb-1.5" data-testid="pin-date-toggle">
            {PIN_DATES().map(d => (
              <button key={d.key} onClick={() => setDate(d.key)} data-testid={`pin-date-${d.label.toLowerCase()}`}
                className={`flex-1 h-8 border-2 text-[10px] font-black uppercase tracking-widest ${date === d.key ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-600 border-slate-300"}`}>
                {d.label}
              </button>
            ))}
          </div>
          {/* Any-date picker so admins can pin from the job page without
              hopping back to Sweeper Radar. Highlights when a non-preset
              day is picked so it's obvious which mode is active. */}
          <div className="mb-2 flex items-center gap-1">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex-shrink-0">Or pick date</label>
            <input type="date" value={date} min={isoOffset(0)}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              data-testid="pin-date-picker"
              className={`flex-1 h-8 px-1.5 border-2 text-[11px] font-mono outline-none focus:border-[#00C4CC] ${
                (date !== isoOffset(0) && date !== isoOffset(1))
                  ? "border-amber-500 bg-amber-50 text-slate-900 font-black"
                  : "border-slate-300 bg-white text-slate-700"
              }`}/>
          </div>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 px-1">Truck</div>
          {/* Nearest-truck hint — suggestion only, still requires a click. */}
          {jobIds?.length === 1 && (
            <div className="mb-1 rounded border border-amber-200 bg-amber-50/60" data-testid="pin-nearest-row">
              {nearestBusy && (
                <div className="h-9 px-2 flex items-center text-[10px] font-black uppercase tracking-widest text-amber-700">
                  <Sparkle size={11} weight="fill" className="mr-1 animate-pulse"/> Finding nearest…
                </div>
              )}
              {!nearestBusy && nearest?.available && (
                <button
                  onClick={() => pin(nearest.truck_key, nearest.truck_label)}
                  disabled={busy}
                  data-testid="pin-nearest-suggest"
                  className="w-full text-left h-11 px-2 flex items-center gap-2 hover:bg-amber-100 rounded">
                  <Sparkle weight="fill" size={14} className="text-amber-600 flex-shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-black text-slate-900 truncate">Nearest: {nearest.truck_label}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      ~{Number(nearest.miles).toFixed(1)} mi · {nearest.sample_count} pinned
                    </div>
                  </div>
                  <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500 text-white flex-shrink-0">
                    Suggested
                  </span>
                </button>
              )}
              {!nearestBusy && nearest && !nearest.available && (
                <div className="h-9 px-2 flex items-center text-[10px] text-slate-500 italic">
                  {nearest.reason || "No nearest suggestion"}
                </div>
              )}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto">
            {techs.map(w => (
              <button key={w.id} onClick={() => pin(w.id, w.name)} disabled={busy} data-testid={`pin-truck-${w.id}`}
                className="w-full text-left h-9 px-2 text-xs font-bold hover:bg-amber-50 inline-flex items-center gap-2">
                <Truck weight="fill" size={14} className="text-slate-500"/> {w.name}
              </button>
            ))}
            {subs.map(s => (
              <button key={s.id} onClick={() => pin(`sub:${s.id}`, s.name)} disabled={busy} data-testid={`pin-truck-sub-${s.id}`}
                className="w-full text-left h-9 px-2 text-xs font-bold hover:bg-fuchsia-50 inline-flex items-center gap-2">
                <Wrench weight="fill" size={14} className="text-fuchsia-600"/> {s.name}
                <span className="ml-auto text-[9px] uppercase text-fuchsia-600">sub</span>
              </button>
            ))}
            {techs.length === 0 && subs.length === 0 && <div className="p-2 text-xs text-slate-500">No trucks available</div>}
          </div>
        </div>
      )}
    </div>
  );
}
